import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { computeFaDesign, fmtMoney, FA_MANUFACTURERS, type DesignInput, type DesignResult } from '../lib/faDesigner';
import { buildFaSubmittalDoc, downloadBlob } from '../lib/faSubmittal';
import { api } from '../api/client';
import { useLang } from '../i18n/lang';

// Fire Alarm Design-Build Wizard (R-2 multi-family). Code basis: NFPA 72 (2022),
// CBC §907 (2022), CFC. Output is a BUDGETARY ROM — not a stamped design.
// Layout matches the reference build: dark form on left, result panel on right.

const OCCUPANCY = [{ v: 'r2_apartments', label: 'R-2 Apartments' }, { v: 'r2_condos', label: 'R-2 Condominiums' }];
const SPRINKLER = [
  { v: '13R', label: 'NFPA 13R (residential)' },
  { v: '13', label: 'NFPA 13 (full)' },
  { v: '13D', label: 'NFPA 13D (one/two family)' },
  { v: 'none', label: 'No sprinkler system' },
];

export function FireAlarmDesigner() {
  const [f, setF] = useState<DesignInput>({
    units: 50, stories: 3, heightFt: 33, grossSf: 52000,
    sprinklerType: '13R', faSystem: 'auto', occupancy: 'r2_apartments',
    prevailingWage: true, atticAccess: false,
  });
  const [result, setResult] = useState<DesignResult | null>(null);
  const [params, setParams] = useSearchParams();
  const [savedId, setSavedId] = useState<string | null>(params.get('project'));
  const [saving, setSaving] = useState<'' | 'saving' | 'saved' | 'error'>('');
  const { t } = useLang();

  const set = <K extends keyof DesignInput>(k: K, v: DesignInput[K]) => setF((p) => ({ ...p, [k]: v }));
  const calculate = () => setResult(computeFaDesign(f));

  // Reopen a saved FA bid: load its inputs and recompute.
  useEffect(() => {
    const pid = params.get('project');
    if (!pid) return;
    api.getProject(pid).then((p) => {
      if (p.fa_input) { const inp = JSON.parse(p.fa_input) as DesignInput; setF((cur) => ({ ...cur, ...inp })); setResult(computeFaDesign(inp)); }
      setSavedId(pid);
    }).catch(() => { /* ignore — start fresh */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveBid() {
    const r = result ?? computeFaDesign(f);
    setSaving('saving');
    try {
      const res = await api.saveFaDesign({
        id: savedId ?? undefined,
        name: f.projectName || 'Fire Alarm Design-Build',
        cityAhj: f.cityAhj,
        input: f,
        sell_price: r.totals.total,
        categories: { material: r.totals.material, labor: r.totals.laborCost, engineering: r.totals.engineering, permit: r.totals.permit, markup: r.totals.markup, contingency: r.totals.contingency },
      });
      setSavedId(res.id);
      setParams({ project: res.id }, { replace: true });
      setSaving('saved'); setTimeout(() => setSaving(''), 2500);
    } catch { setSaving('error'); }
  }

  return (
    <div className="fa-wrap">
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-tag">CALIFORNIA · NFPA 72 (2022) · CBC §907</div>
        <h1 className="fa-header-title">🔥 Fire Alarm Design-Build Wizard <span className="fa-header-sub">— Budgetary ROM only</span></h1>
        <Link to="/" className="fa-close" aria-label={t({ en: 'Close', fil: 'Isara' })}>✕</Link>
      </div>

      <div className="fa-body">
        {/* LEFT — INPUTS */}
        <div className="fa-form">
          <div className="fa-section">{t({ en: 'Project Inputs', fil: 'Mga Input ng Proyekto' })}</div>

          <FaField label={t({ en: 'Project Name', fil: 'Pangalan ng Proyekto' })}>
            <input value={f.projectName ?? ''} onChange={(e) => set('projectName', e.target.value)} placeholder="e.g. Magnolia Apartments Phase II" />
          </FaField>
          <FaField label="City / AHJ">
            <input value={f.cityAhj ?? ''} onChange={(e) => set('cityAhj', e.target.value)} placeholder="e.g. San Jose Fire Department" />
          </FaField>
          <FaField label="Fire Alarm System (Panel / Manufacturer)">
            <select value={f.faSystem ?? 'auto'} onChange={(e) => set('faSystem', e.target.value)}>
              {FA_MANUFACTURERS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </FaField>
          <FaField label="Occupancy Type">
            <select value={f.occupancy ?? 'r2_apartments'} onChange={(e) => set('occupancy', e.target.value as DesignInput['occupancy'])}>
              {OCCUPANCY.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </FaField>

          <div className="fa-row-2">
            <FaField label="Dwelling Units">
              <input type="number" value={f.units} onChange={(e) => set('units', +e.target.value)} />
            </FaField>
            <FaField label="Stories Above Grade">
              <input type="number" value={f.stories} onChange={(e) => set('stories', +e.target.value)} />
            </FaField>
          </div>
          <div className="fa-row-2">
            <FaField label="Building Height (ft)">
              <input type="number" value={f.heightFt ?? ''} onChange={(e) => set('heightFt', +e.target.value)} />
            </FaField>
            <FaField label="Gross SF (total)">
              <input type="number" value={f.grossSf ?? ''} onChange={(e) => set('grossSf', +e.target.value)} />
            </FaField>
          </div>
          <FaField label="Sprinkler System">
            <select value={f.sprinklerType ?? '13R'} onChange={(e) => set('sprinklerType', e.target.value as DesignInput['sprinklerType'])}>
              {SPRINKLER.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </FaField>

          <div className="fa-section fa-section-sub">{t({ en: 'Refinements (optional — defaults will be calculated)', fil: 'Mga Refinement (opsyonal — auto-kakalkulahin ang mga default)' })}</div>
          <div className="fa-row-2">
            <FaField label="Common-area SF"><input placeholder="auto" value={f.commonSf ?? ''} onChange={(e) => set('commonSf', +e.target.value || undefined)} /></FaField>
            <FaField label="Corridor LF (total)"><input placeholder="auto" value={f.corridorLf ?? ''} onChange={(e) => set('corridorLf', +e.target.value || undefined)} /></FaField>
          </div>
          <div className="fa-row-3">
            <FaField label="Stairs"><input placeholder="auto" value={f.stairs ?? ''} onChange={(e) => set('stairs', +e.target.value || undefined)} /></FaField>
            <FaField label="Exits"><input placeholder="auto" value={f.exits ?? ''} onChange={(e) => set('exits', +e.target.value || undefined)} /></FaField>
            <FaField label="HVAC units >2000 CFM"><input placeholder="auto" value={f.hvacUnits ?? ''} onChange={(e) => set('hvacUnits', +e.target.value || undefined)} /></FaField>
          </div>
          <div className="fa-row-3">
            <FaField label="Elev. Lobbies"><input placeholder="auto" value={f.elevatorLobbies ?? ''} onChange={(e) => set('elevatorLobbies', +e.target.value || undefined)} /></FaField>
            <FaField label="Mech. Rooms"><input placeholder="auto" value={f.mechanicalRooms ?? ''} onChange={(e) => set('mechanicalRooms', +e.target.value || undefined)} /></FaField>
            <FaField label="Elec. Rooms"><input placeholder="auto" value={f.electricalRooms ?? ''} onChange={(e) => set('electricalRooms', +e.target.value || undefined)} /></FaField>
          </div>

          <label className="fa-check">
            <input type="checkbox" checked={!!f.atticAccess} onChange={(e) => set('atticAccess', e.target.checked)} />
            <span>{t({ en: 'Accessible attic spaces (adds heat detectors per story)', fil: 'May accessible na attic spaces (nagdadagdag ng heat detectors kada story)' })}</span>
          </label>
          <label className="fa-check">
            <input type="checkbox" checked={!!f.prevailingWage} onChange={(e) => set('prevailingWage', e.target.checked)} />
            <span>California prevailing wage (public works / publicly-funded)</span>
          </label>

          <button className="fa-calc" onClick={calculate}>⚡ {t({ en: 'CALCULATE DESIGN', fil: 'KALKULAHIN ANG DESIGN' })}</button>
        </div>

        {/* RIGHT — RESULT */}
        <div className="fa-result">
          {!result && (
            <div className="fa-result-empty">
              <div style={{ fontSize: 56 }}>🔥</div>
              <p>{t({ en: 'Fill in the inputs on the left and click', fil: 'Punan ang mga input sa kaliwa at i-click ang' })} <strong style={{ color: 'var(--fa-accent)' }}>{t({ en: 'Calculate Design', fil: 'Calculate Design' })}</strong>.</p>
              <p className="muted">{t({ en: 'The wizard generates a complete BOM, budgetary ROM, scope letter, RFI checklist, and submittal-package outline.', fil: 'Bumubuo ang wizard ng kumpletong BOM, budgetary ROM, scope letter, RFI checklist, at submittal-package outline.' })}</p>
            </div>
          )}
          {result && <DesignOutput r={result} onSave={saveBid} saving={saving} savedId={savedId} />}
        </div>
      </div>
    </div>
  );
}

function FaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="fa-field">
      <span className="fa-field-label">{label}</span>
      {children}
    </label>
  );
}

function DesignOutput({ r, onSave, saving, savedId }: { r: DesignResult; onSave: () => void; saving: string; savedId: string | null }) {
  const f = r.flags;
  const { t } = useLang();
  function exportSubmittal() {
    const today = new Date().toISOString().slice(0, 10);
    const blob = buildFaSubmittalDoc(r, today);
    const name = (r.input.projectName || 'FA_Submittal').replace(/[^\w-]+/g, '_').slice(0, 60);
    downloadBlob(blob, `${name}_FA_Submittal_for_PE_Stamp.doc`);
  }
  return (
    <div className="fa-out">
      <div className="fa-out-disclaimer">⚠️ {t({ en: 'Budgetary ROM only — not a stamped design. Final design by licensed FA engineer; AHJ submittal required.', fil: 'Budgetary ROM lang — hindi ito stamped design. Ang final design ay gagawin ng licensed FA engineer; kailangan ng AHJ submittal.' })}</div>
      <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="fa-calc" style={{ width: 'auto', padding: '10px 22px' }} onClick={onSave} disabled={saving === 'saving'}>
          {saving === 'saved' ? t({ en: '✓ Saved to bids', fil: '✓ Na-save sa bids' }) : saving === 'saving' ? t({ en: 'Saving…', fil: 'Sine-save…' }) : savedId ? t({ en: '💾 Update saved bid', fil: '💾 I-update ang naka-save na bid' }) : t({ en: '💾 Save as bid', fil: '💾 I-save bilang bid' })}
        </button>
        <button className="fa-calc" style={{ width: 'auto', padding: '10px 22px' }} onClick={exportSubmittal}>
          📄 {t({ en: 'Export PE-ready Submittal Package (Word)', fil: 'I-export ang PE-ready Submittal Package (Word)' })}
        </button>
        {saving === 'error' && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{t({ en: 'Save failed — try again.', fil: 'Nabigo ang pag-save — subukan ulit.' })}</span>}
        {savedId && saving !== 'saving' && <span className="fa-mut" style={{ fontSize: 12 }}>{t({ en: 'Appears on your Dashboard · reopen anytime', fil: 'Lalabas sa iyong Dashboard · mabubuksan anumang oras' })}</span>}
      </div>

      <div className="fa-out-summary">
        <div><div className="fa-mut">Sell price</div><div className="fa-big">{fmtMoney(r.totals.total)}</div></div>
        <div><div className="fa-mut">{t({ en: 'Per unit', fil: 'Kada unit' })}</div><div className="fa-big">{fmtMoney(r.totals.perUnit)}</div></div>
        <div><div className="fa-mut">Material</div><div>{fmtMoney(r.totals.material)}</div></div>
        <div><div className="fa-mut">Labor</div><div>{fmtMoney(r.totals.laborCost)} <span className="muted">({r.totals.hours} hrs @ ${r.laborRate}/hr)</span></div></div>
        <div><div className="fa-mut">PE-stamped design pkg</div><div>{fmtMoney(r.totals.engineering)} <span className="muted">(~{r.totals.sheets} sheets + calcs)</span></div></div>
        <div><div className="fa-mut">AHJ permit allowance</div><div>{fmtMoney(r.totals.permit)}</div></div>
        <div><div className="fa-mut">Markup (22%)</div><div>{fmtMoney(r.totals.markup)}</div></div>
        <div><div className="fa-mut">Contingency (10%)</div><div>{fmtMoney(r.totals.contingency)}</div></div>
      </div>

      <div className="fa-out-flags">
        {f.isHighRise && <span className="pill pill-needs_review">High-rise (≥75 ft)</span>}
        {f.requiresVoiceEvac && <span className="pill pill-needs_review">Voice/EVACS required</span>}
        {f.isLargePanel && <span className="pill pill-proposed">Large panel</span>}
        {f.requiresSystem && <span className="pill pill-confirmed">FA system required by code</span>}
      </div>

      <h2 className="fa-out-h">Bill of Materials</h2>
      <table>
        <thead><tr><th>{t({ en: 'Description', fil: 'Paglalarawan' })}</th><th>Qty</th><th>Unit $</th><th>Ext $</th><th>Unit hrs</th><th>Ext hrs</th></tr></thead>
        <tbody>
          {r.bom.map((b, i) => (
            <tr key={i}>
              <td>{b.description}</td>
              <td>{b.quantity.toLocaleString()}</td>
              <td>{fmtMoney(b.unit_material)}</td>
              <td>{fmtMoney(b.ext_material)}</td>
              <td>{b.unit_hours.toFixed(2)}</td>
              <td>{b.ext_hours.toFixed(1)}</td>
            </tr>
          ))}
          <tr><td colSpan={3}><strong>Subtotal</strong></td><td><strong>{fmtMoney(r.totals.material)}</strong></td><td /><td><strong>{r.totals.hours}</strong></td></tr>
        </tbody>
      </table>

      <h2 className="fa-out-h">Code Basis</h2>
      <ul className="fa-codes">
        <li><strong>NFPA 72 (2022)</strong> — System design, devices, notification, EVACS, ITM (§10/12/14/17/18/21/23/24/26).</li>
        <li><strong>CBC §907</strong> — Where required (§907.2.9 R-2 corridor smoke; §907.2.13 high-rise; §907.5.2 notification).</li>
        <li><strong>NFPA 90A</strong> — Duct smoke detection (&gt;2000 CFM).</li>
        <li><strong>NFPA 70 Art. 760</strong> — FA wiring, pathways (Class A/B/X), FPLP cable, separation.</li>
        <li><strong>NFPA 20 / 13</strong> — Fire pump supervision; sprinkler waterflow/tamper.</li>
        <li><strong>CBC §1009.8</strong> — Area-of-Refuge two-way comms (accessible egress).</li>
        <li><strong>CBC §915</strong> — Carbon monoxide detection.</li>
        <li><strong>CBC §3008 / ASME A17.1 / NFPA 72 §21.4</strong> — Elevator recall + hoistway.</li>
        <li><strong>UL 864 / 268 / 268A / 521 / 464 / 1971 / 1481 / 2017 / 2572</strong> — Equipment listings.</li>
      </ul>

      <p className="muted" style={{ marginTop: 12 }}>
        {t({ en: 'See the full', fil: 'Tingnan ang buong' })} <Link to="/codes">Codes & Standards Library →</Link> {t({ en: 'for all references attached to a proposal.', fil: 'para sa lahat ng references na nakakabit sa isang proposal.' })}
      </p>
    </div>
  );
}
